import React from 'react'
import { Box, Typography, LinearProgress, Tooltip } from '@mui/material'
import { pluralize } from '../../helper/text'
import { getTextShadow } from '../../helper/progress'

const TaskProgressBar = ({
  completed,
  total,
  completedValue,
  totalValue,
  progress,
  status,
  pendingCount = 0,
  dueDate,
  daysLeft
}) => {

  // ---- normalize values (prefer BE fields) ----
  const safeCompleted = Number.isFinite(Number(completedValue))
    ? Number(completedValue)
    : Number(completed || 0)

  const safeTotal = Number.isFinite(Number(totalValue))
    ? Number(totalValue)
    : Number(total || 0)

  // if caller didn't pass progress, compute it from BE values
  const safeProgress =
    Number.isFinite(Number(progress))
      ? Number(progress)
      : (safeTotal > 0 ? Math.round((safeCompleted / safeTotal) * 100) : 0)


  // normalize daysLeft once
  const rawDays = daysLeft === undefined || daysLeft === null
    ? NaN
    : (typeof daysLeft === 'string' ? parseInt(daysLeft, 10) : Number(daysLeft))

  // derive a signed days-left value. Some callers pass Math.abs(days) which
  // loses the sign for overdue items. When days appear absolute, try to
  // recompute the signed difference from `dueDate` if available.
  let days = rawDays
  if (!Number.isNaN(rawDays) && rawDays >= 0 && dueDate) {
    const parsed = new Date(dueDate)
    if (!Number.isNaN(parsed.getTime())) {
      const today = new Date()
      // compute ceil difference (deadline - today) in days
      const diff = Math.ceil((parsed.setHours(0, 0, 0, 0) - new Date(today.setHours(0, 0, 0, 0))) / (1000 * 60 * 60 * 24))
      days = Number.isNaN(diff) ? rawDays : diff
    }
  }

  // duedate dot indicator
  const getDueDateColor = () => {
    if (Number.isNaN(days)) return '#2666be' // default blue
    if (days < 0) return '#EB5757' // overdue -> red
    if (days === 0) return '#eb5757' // due today -> red
    if (days <= 3) return '#eb5757' // near due -> red
    return '#2666be'
  }

  return (
    <Box>
      {/* Icon + Progress Fraction - Same Row */}
      <Box display="flex" flexDirection="row" alignItems="center" justifyItems="center" gap={1} mb={1}>

        <Tooltip title="Completed" arrow placement="bottom">
          <span style={{ display: "flex" }}>
            {/* Target Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <g clipPath="url(#clip0_1227_11776)">
                <path d="M17.0463 2.95378L19.1412 3.37253C19.2537 3.39503 19.3463 3.47753 19.3787 3.58878C19.3952 3.64282 19.3966 3.7003 19.383 3.7551C19.3693 3.80991 19.3411 3.85999 19.3013 3.90003L17.585 5.61753C17.3794 5.82202 17.1013 5.93703 16.8113 5.93753H15.3875L11.8125 9.51378C11.8807 9.7713 11.8932 10.0404 11.8493 10.3032C11.8053 10.5659 11.7059 10.8163 11.5576 11.0376C11.4093 11.2589 11.2155 11.4461 10.9892 11.5866C10.7629 11.7272 10.5092 11.8179 10.2451 11.8527C9.98099 11.8875 9.71248 11.8657 9.45747 11.7886C9.20246 11.7115 8.96681 11.581 8.7662 11.4057C8.5656 11.2304 8.40465 11.0143 8.29409 10.772C8.18353 10.5296 8.12588 10.2664 8.125 10C8.12494 9.71231 8.19109 9.42843 8.31834 9.17038C8.44559 8.91233 8.63052 8.68703 8.85881 8.51191C9.0871 8.33679 9.35263 8.21656 9.63484 8.16053C9.91705 8.1045 10.2084 8.11416 10.4862 8.18878L14.0625 4.61128V3.19003C14.0625 2.90003 14.1775 2.62128 14.3825 2.41628L16.1 0.698777C16.14 0.658938 16.1901 0.630693 16.2449 0.617047C16.2997 0.603401 16.3572 0.604862 16.4112 0.621277C16.5225 0.653777 16.605 0.746277 16.6275 0.858777L17.0463 2.95378Z" fill="#2666BE" />
                <path d="M2.50011 9.99999C2.5014 11.0724 2.73267 12.1321 3.17832 13.1076C3.62398 14.0831 4.27365 14.9516 5.08352 15.6547C5.89339 16.3577 6.84462 16.8789 7.87303 17.1831C8.90145 17.4872 9.98314 17.5673 11.0451 17.4179C12.1071 17.2684 13.1247 16.893 14.0293 16.3168C14.9338 15.7407 15.7043 14.9772 16.2887 14.078C16.8731 13.1788 17.2579 12.1647 17.417 11.1041C17.5762 10.0435 17.5061 8.96116 17.2114 7.92999C17.17 7.80952 17.1536 7.68189 17.1631 7.55487C17.1726 7.42785 17.2079 7.30409 17.2668 7.19114C17.3256 7.07818 17.4069 6.97839 17.5056 6.89784C17.6042 6.81729 17.7183 6.75766 17.8407 6.72259C17.9632 6.68752 18.0915 6.67773 18.2179 6.69383C18.3442 6.70993 18.466 6.75158 18.5757 6.81624C18.6855 6.8809 18.7809 6.96721 18.8562 7.06994C18.9316 7.17266 18.9852 7.28963 19.0139 7.41374C19.5845 9.41065 19.4761 11.5405 18.7057 13.4692C17.9352 15.3978 16.5463 17.0161 14.7569 18.0703C12.9674 19.1244 10.8787 19.5546 8.8183 19.2935C6.75793 19.0324 4.84254 18.0947 3.37261 16.6275C1.90429 15.1579 0.965583 13.2424 0.703869 11.1815C0.442155 9.12067 0.872255 7.03126 1.92664 5.24133C2.98103 3.45141 4.6 2.06232 6.52936 1.29217C8.45872 0.522017 10.5892 0.414411 12.5864 0.986244C12.8242 1.05576 13.0248 1.21658 13.1444 1.43356C13.264 1.65054 13.2928 1.90604 13.2246 2.14421C13.1564 2.38239 12.9966 2.58388 12.7803 2.70466C12.564 2.82543 12.3087 2.85566 12.0701 2.78874C10.9533 2.46805 9.77731 2.41123 8.6348 2.62274C7.4923 2.83426 6.41457 3.30834 5.48661 4.00758C4.55864 4.70682 3.80584 5.61211 3.28756 6.65203C2.76929 7.69196 2.49972 8.83808 2.50011 9.99999Z" fill="#2666BE" />
                <path d="M6.25011 10C6.25018 10.6833 6.43679 11.3535 6.78981 11.9385C7.14283 12.5235 7.64886 13.001 8.25332 13.3195C8.85778 13.638 9.53773 13.7854 10.2198 13.7458C10.9019 13.7063 11.5603 13.4813 12.1239 13.095C12.6874 12.7078 13.1343 12.174 13.4163 11.5511C13.6983 10.9283 13.8046 10.2402 13.7239 9.56129C13.702 9.39654 13.7245 9.22895 13.7889 9.07577C13.8534 8.92258 13.9575 8.78934 14.0905 8.68977C14.2236 8.5902 14.3808 8.52789 14.5459 8.50926C14.7111 8.49064 14.8782 8.51636 15.0301 8.58379C15.182 8.65035 15.3135 8.75599 15.4112 8.88994C15.5089 9.02389 15.5694 9.18136 15.5864 9.34629C15.7222 10.5064 15.4935 11.6801 14.932 12.7043C14.3705 13.7285 13.5041 14.5526 12.453 15.0621C11.402 15.5716 10.2183 15.7412 9.06648 15.5474C7.91461 15.3537 6.85163 14.8062 6.02506 13.9809C5.1985 13.1556 4.64936 12.0934 4.45386 10.9418C4.25837 9.79026 4.42621 8.60639 4.93409 7.55454C5.44198 6.50269 6.26471 5.63503 7.2881 5.072C8.31149 4.50897 9.48478 4.27848 10.6451 4.41254C10.7694 4.42407 10.89 4.46028 11.0001 4.51906C11.1102 4.57784 11.2074 4.658 11.2861 4.75484C11.3648 4.85168 11.4233 4.96325 11.4583 5.08302C11.4934 5.20278 11.5041 5.32833 11.49 5.45231C11.4758 5.57628 11.4371 5.69618 11.376 5.80499C11.3149 5.91379 11.2327 6.0093 11.1342 6.08593C11.0357 6.16256 10.923 6.21875 10.8025 6.25123C10.682 6.2837 10.5563 6.2918 10.4326 6.27504C9.90718 6.21404 9.37479 6.26488 8.87041 6.42423C8.36602 6.58358 7.90105 6.84783 7.50604 7.19964C7.11104 7.55144 6.79492 7.98283 6.57846 8.46547C6.36201 8.94811 6.25011 9.47108 6.25011 10Z" fill="#2666BE" />
              </g>
              <defs>
                <clipPath id="clip0_1227_11776">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
      </Tooltip>

        {/* Progress Bar with Single Counter */}
        <Box sx={{ position: 'relative', width: '100%' }}>
          {/* Background Bar - Light Blue */}
          <Box
            sx={{
              width: '100%',
              height: 20,
              backgroundColor: '#deecff',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Progress Fill - Blue */}
            <Box
              sx={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor: '#2666be',
                transition: 'width 0.3s ease',
                position: 'absolute',
                top: 0,
                left: 0,
                borderRadius: 2
              }}
            />
          </Box>

          {/* Single Counter - Positioned in Center */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                fontSize: '12px',
                // Change color based on progress
                // If progress > 50%, text is likely covered by blue, so use white
                // Otherwise use black
                color: progress > 50 ? '#FFFFFF' : '#3a3a3a',
                transition: 'color 0.3s ease',
                userSelect: 'none',
                // textShadow: safeProgress > 40 && safeProgress < 60
                //   ? '0 0 2px rgba(0,0,0,0.3)'
                //   : 'none', // Add shadow during transition
                textShadow: getTextShadow(safeProgress),
                zIndex: 10
              }}
            >
              {/* {completed}/{total} */}
              {/* {safeTotal}/{safeCompleted} */}
              {safeCompleted}/{safeTotal}
            </Typography>
          </Box>
        </Box>

      </Box>

      {/* Due Date Section */}
      {dueDate && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: getDueDateColor(),
                flexShrink: 0
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: days < 0 ? '#EB5757' : 'text.secondary',
                fontWeight: days < 0 ? 700 : 400
              }}
            >
              Due date: {dueDate}
            </Typography>
          </Box>
          {(() => {
            if (Number.isNaN(days)) return null

            // days === 0 => Due today (amber)
            if (days === 0) {
              return (
                <Box display="flex" alignItems="center" gap={1} ml={2.5}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#eb5757',
                      fontWeight: 500,
                      display: 'block'
                    }}
                  >
                    Due today
                  </Typography>
                </Box>
              )
            }

            // days < 0 => overdue by N days + OVERDUE tag (red)
            if (days < 0) {
              return (
                <Box display="flex" alignItems="center" gap={1} ml={2.5}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#EB5757',
                      fontWeight: 400,
                      display: 'block'
                    }}
                  >
                    {/* {`${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`} */}
                    {pluralize(Math.abs(days), 'day')}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#EB5757',
                      fontWeight: 400,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      bgcolor: 'rgba(235,87,87,0.08)',
                      px: 0.8,
                      py: 0.25,
                      borderRadius: 1
                    }}
                  >
                    OVERDUE
                  </Typography>
                </Box>
              )
            }

            // days > 0 => normal days left
            return (
              <Typography
                variant="caption"
                sx={{
                  color: getDueDateColor(),
                  ml: 2.5,
                  fontWeight: 500,
                  display: 'block'
                }}
              >
                {`${days} day${days === 1 ? '' : 's'} left`}
              </Typography>
            )
          })()}
        </Box>
      )}
    </Box>
  )
}

export default TaskProgressBar